import type {
	ActivateCardBody,
	ActivateCardsBody,
	ActivateCardsResult,
	ActivationBatchDetail,
	ActivationBatchesPage,
	CardDetail,
	CardIncident,
	CardsFilter,
	CardsListPage,
	CardsSummary,
	CreatePrintBatchBody,
	CreatePrintBatchResult,
	PrintBatchCardsPage,
	PrintBatchDetail,
	PrintBatchGeneratedBody,
	PrintBatchKind,
	PrintBatchMoveResult,
	PrintBatchReceivedBody,
	PrintBatchesPage,
	ReportCardIncidentBody,
} from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { queryClient } from "./query-client";

/**
 * E-Accreditation (M7a slice 1): the physical card's life after approval —
 * cards, print batches, activation, lost reports. Bodies, queries and
 * results are `@seagames/contracts` (`e-accreditation.ts`); one file, so the
 * query keys and their invalidation live together.
 */

/** The cards list's filter in WIRE form (`cardsListQuery` keys); `card_state` is the comma list the URL and the API both read. */
export interface CardsWireFilter {
	games_id: string;
	card_state?: string;
	responsible_organization_id?: string;
	main_function_id?: string;
	sport_id?: string;
	print_batch_id?: string;
	q?: string;
}

/** Compile-time check: this wire filter is what the bulk endpoints accept as `filter`. */
const filterFitsBulk: CardsWireFilter extends CardsFilter ? true : never = true;
void filterFitsBulk;

/** Drop unset keys so equal filters make equal query keys. */
const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "")) as T;

export const eAccKeys = {
	all: ["e-accreditation"] as const,
	cards: (q: object) => ["e-accreditation", "cards", clean(q)] as const,
	summary: (gamesId: string, orgId?: string) => ["e-accreditation", "summary", gamesId, orgId ?? null] as const,
	card: (id: string) => ["e-accreditation", "card", id] as const,
	printBatches: (q: object) => ["e-accreditation", "print-batches", clean(q)] as const,
	printBatch: (id: string) => ["e-accreditation", "print-batch", id] as const,
	activationBatches: (q: object) => ["e-accreditation", "activation-batches", clean(q)] as const,
	activationBatch: (id: string) => ["e-accreditation", "activation-batch", id] as const,
};

/** `GET /cards` — one keyset page (newest number first); the total rides on the first page only. */
export const cardsListOptions = (q: CardsWireFilter & { cursor?: string; limit: number }) =>
	queryOptions({
		queryKey: eAccKeys.cards(q),
		queryFn: ({ signal }) => api.get<CardsListPage>("/cards", { query: { ...clean(q) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
		enabled: !!q.games_id,
	});

/** `GET /cards/summary` — one count per state, for the games (and one organisation). */
export const cardsSummaryOptions = (gamesId: string | undefined, orgId?: string) =>
	queryOptions({
		queryKey: eAccKeys.summary(gamesId ?? "", orgId),
		queryFn: ({ signal }) =>
			api.get<CardsSummary>("/cards/summary", { query: clean({ games_id: gamesId ?? "", responsible_organization_id: orgId }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
		enabled: !!gamesId,
	});

/** `GET /cards/:id` — card, live issue, access, last incident and what the caller may do. */
export const cardDetailOptions = (id: string) =>
	queryOptions({
		queryKey: eAccKeys.card(id),
		queryFn: ({ signal }) => api.get<CardDetail>(`/cards/${id}`, { signal }),
	});

/** `GET /cards/lookup?q=` — a QR token or a card number → one card, or 404. Host side only (a NOC gets 403). */
export const lookupCard = (q: string, gamesId?: string) => api.get<CardDetail>("/cards/lookup", { query: clean({ q, games_id: gamesId }) });

/** `GET /print-batches` — keyset, newest first. */
export const printBatchesOptions = (q: { games_id: string; cursor?: string; limit: number }) =>
	queryOptions({
		queryKey: eAccKeys.printBatches(q),
		queryFn: ({ signal }) => api.get<PrintBatchesPage>("/print-batches", { query: { ...clean(q) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
		enabled: !!q.games_id,
	});

/** `GET /print-batches/:id` — one batch, its organisations and what this login may do (the sheet). */
export const printBatchOptions = (id: string) =>
	queryOptions({
		queryKey: eAccKeys.printBatch(id),
		queryFn: ({ signal }) => api.get<PrintBatchDetail>(`/print-batches/${id}`, { signal }),
	});

/** `GET /print-batches/:id/cards` — one page of the batch for the card generator (number order, full photos, QR tokens). Not cached: the photo URLs are signed per download. */
export const fetchPrintBatchCards = (id: string, q: { cursor?: string; limit: number }, signal?: AbortSignal) =>
	api.get<PrintBatchCardsPage>(`/print-batches/${id}/cards`, { query: { ...clean(q) }, signal });

/** `GET /activation-batches/:id` — the batch and the cards it activated that the caller can see. */
export const activationBatchOptions = (id: string) =>
	queryOptions({
		queryKey: eAccKeys.activationBatch(id),
		queryFn: ({ signal }) => api.get<ActivationBatchDetail>(`/activation-batches/${id}`, { signal }),
	});

/** `GET /activation-batches` — keyset, newest first (host only). */
export const activationBatchesOptions = (q: { games_id: string; cursor?: string; limit: number }) =>
	queryOptions({
		queryKey: eAccKeys.activationBatches(q),
		queryFn: ({ signal }) => api.get<ActivationBatchesPage>("/activation-batches", { query: { ...clean(q) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
		enabled: !!q.games_id,
	});

/**
 * After any card write: every card list, summary, card and batch list may
 * have moved — and so may the records pages, which show the same cards.
 */
export const invalidateEAccreditation = () =>
	Promise.all([queryClient.invalidateQueries({ queryKey: eAccKeys.all }), queryClient.invalidateQueries({ queryKey: ["accreditations"] })]);

// ── Writes ──────────────────────────────────────────────────────────────────

/** Either explicit ids, or the list's filter with the count the user saw ("Select all N"). */
export type CardsTarget = { ids: string[] } | { filter: CardsWireFilter; expected_count: number };

/** `POST /print-batches` — one live issue per printable card; the rest come back in `skipped`. */
export const createPrintBatch = (target: CardsTarget, notes?: string, kind: PrintBatchKind = "HOST") =>
	api.post<CreatePrintBatchResult>("/print-batches", { ...target, kind, ...(notes ? { notes } : {}) } satisfies CreatePrintBatchBody);

/** `POST /print-batches/:id/generated` — the browser drew every card of the batch (`expected_count`) into its ZIP. */
export const markPrintBatchGenerated = (printBatchId: string, expectedCount: number) =>
	api.post<PrintBatchMoveResult>(`/print-batches/${printBatchId}/generated`, { expected_count: expectedCount } satisfies PrintBatchGeneratedBody);

/** `POST /print-batches/:id/send-to-printer` — GENERATED → PRINTING, every card with it. */
export const sendPrintBatchToPrinter = (printBatchId: string) => api.post<PrintBatchMoveResult>(`/print-batches/${printBatchId}/send-to-printer`, {});

/** `POST /print-batches/:id/mark-printed` — back from the printer: PRINTING → PRINTED, every card with it. */
export const markPrintBatchPrinted = (printBatchId: string) => api.post<PrintBatchMoveResult>(`/print-batches/${printBatchId}/mark-printed`, {});

/** `POST /print-batches/:id/mark-received` — the batch's PRINTED cards (one organisation's when given) → received. */
export const markPrintBatchReceived = (printBatchId: string, organizationId?: string) =>
	api.post<PrintBatchMoveResult>(`/print-batches/${printBatchId}/mark-received`, (organizationId ? { responsible_organization_id: organizationId } : {}) satisfies PrintBatchReceivedBody);

/** `POST /cards/activate` — PRINTED / COLLECTED → ACTIVE until `valid_until`; the rest come back in `skipped`. */
export const activateCards = (target: CardsTarget, validUntil: string | undefined) =>
	api.post<ActivateCardsResult>("/cards/activate", { ...target, ...(validUntil ? { valid_until: validUntil } : {}) } satisfies ActivateCardsBody);

/**
 * `POST /cards/:id/activate` — one card (the card page, the lookup sheet): the same statement as the
 * bulk activate, but refused (404 / 409 CARD_STATE_INVALID) instead of skipped.
 */
export const activateCard = (cardId: string, validUntil: string | undefined) =>
	api.post<ActivateCardsResult>(`/cards/${cardId}/activate`, (validUntil ? { valid_until: validUntil } : {}) satisfies ActivateCardBody);

/** `POST /cards/:id/incidents` — lost / stolen / damaged: the card goes LOST at once (undo within the window). */
export const reportCardIncident = (cardId: string, body: ReportCardIncidentBody) => api.post<CardIncident>(`/cards/${cardId}/incidents`, body);

/** `POST /cards/:id/incidents/:incidentId/undo` — within CARD_INCIDENT_UNDO_MINUTES the card returns to its previous state. */
export const undoCardIncident = (cardId: string, incidentId: string) => api.post<CardIncident>(`/cards/${cardId}/incidents/${incidentId}/undo`, {});
